import { ThemeEvents } from '@theme/events';

/* Click-to-open header dropdown (snippets/header-dropdown.liquid).
   The panel is a popover, so the browser supplies light dismiss, Esc and focus
   order. Natively it renders in the top layer, which escapes the header's
   clipping overflow list; browsers without native popover run Horizon's
   popover-polyfill.js, where the panel stays inside `.header` (`contain:
   layout`), so #place measures the panel's positioning origin instead of
   assuming the viewport. At 990px and up Horizon scrolls `.page-wrapper`, not
   the window, so scroll is tracked in the capture phase on the document. */
const GAP_BELOW_HEADER = 6;
const VIEWPORT_MARGIN = 12;
const MIN_PANEL_HEIGHT = 160;
const SCROLL_OPTIONS = { capture: true, passive: true };
// The polyfill adds `popover` to HTMLElement but cannot teach the CSS parser this selector.
const NATIVE_POPOVER = typeof CSS !== 'undefined' && CSS.supports?.('selector(:popover-open)');

class HeaderDropdown extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #toggle = null;
  /** @type {HTMLElement | null} */
  #panel = null;
  /** @type {HTMLElement | null} */
  #headerMenu = null;

  connectedCallback() {
    this.#toggle = this.querySelector('.header-dropdown__toggle');
    this.#panel = this.querySelector('.header-dropdown__panel');
    if (!this.#toggle || !this.#panel) return;

    this.#panel.addEventListener('beforetoggle', this.#onBeforeToggle);
    this.#panel.addEventListener('toggle', this.#onToggle);
    this.#panel.addEventListener('keydown', this.#onPanelKeydown);
    this.addEventListener('focusout', this.#onFocusOut);

    // Horizon's own panels (the "More" overflow) announce themselves with this
    // event; only one menu should be open at a time.
    this.#headerMenu = this.closest('header-menu');
    this.#headerMenu?.addEventListener(ThemeEvents.megaMenuHover, this.#close);

    // popovertarget works before this module loads, so the panel may already be open.
    if (this.#panel.matches(':popover-open')) this.#opened();
  }

  disconnectedCallback() {
    this.#panel?.removeEventListener('beforetoggle', this.#onBeforeToggle);
    this.#panel?.removeEventListener('toggle', this.#onToggle);
    this.#panel?.removeEventListener('keydown', this.#onPanelKeydown);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.#headerMenu?.removeEventListener(ThemeEvents.megaMenuHover, this.#close);
    this.#headerMenu = null;
    this.#stopTracking();
  }

  /** @param {ToggleEvent} event */
  #onBeforeToggle = (event) => {
    if (event.newState === 'open') this.#place();
  };

  /** @param {ToggleEvent} event */
  #onToggle = (event) => {
    if (event.newState === 'open') {
      this.#opened();
    } else {
      this.#toggle?.setAttribute('aria-expanded', 'false');
      this.#stopTracking();
    }
  };

  #opened() {
    this.#toggle?.setAttribute('aria-expanded', 'true');
    this.#place();
    document.addEventListener('scroll', this.#place, SCROLL_OPTIONS);
    window.addEventListener('resize', this.#onResize);
  }

  #stopTracking() {
    document.removeEventListener('scroll', this.#place, SCROLL_OPTIONS);
    window.removeEventListener('resize', this.#onResize);
  }

  /* The top-layer panel follows the toggle in tab order, but Shift+Tab from its
     first link would otherwise wrap to its last link instead of the toggle. */
  /** @param {KeyboardEvent} event */
  #onPanelKeydown = (event) => {
    if (event.key !== 'Tab' || !event.shiftKey) return;
    if (event.target === this.#panel?.querySelector('a[href]')) {
      event.preventDefault();
      this.#toggle?.focus();
    }
  };

  /* Popovers stay open when focus moves on, so close once keyboard focus leaves
     both the toggle and the panel (the panel is still a DOM descendant). */
  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && !this.contains(event.relatedTarget)) this.#close();
  };

  /* The inline menu is hidden when the header switches to the drawer, so close
     rather than leave a panel pointing at nothing. */
  #onResize = () => {
    if (!this.#toggle?.getClientRects().length) {
      this.#close();
      return;
    }
    this.#place();
  };

  #place = () => {
    const toggle = this.#toggle;
    const panel = this.#panel;
    if (!toggle || !panel) return;

    const header = this.closest('header-component');
    const bottom = Math.max(toggle.getBoundingClientRect().bottom, header ? header.getBoundingClientRect().bottom : 0);
    const title = toggle.querySelector('.menu-list__link-title');
    const anchorLeft = (title ?? toggle).getBoundingClientRect().left;

    const top = Math.round(bottom + GAP_BELOW_HEADER);
    panel.style.maxHeight = `${Math.max(MIN_PANEL_HEIGHT, window.innerHeight - top - VIEWPORT_MARGIN)}px`;

    const panelWidth = panel.offsetWidth;
    const maxLeft = window.innerWidth - panelWidth - VIEWPORT_MARGIN;
    const left = panelWidth ? Math.max(VIEWPORT_MARGIN, Math.min(anchorLeft, maxLeft)) : anchorLeft;

    // In the top layer top/left are viewport coordinates. Polyfilled, they are
    // relative to the containing block, so measure where 0/0 lands.
    let origin = { top: 0, left: 0 };
    if (!NATIVE_POPOVER && panel.getClientRects().length) {
      panel.style.top = '0px';
      panel.style.left = '0px';
      origin = panel.getBoundingClientRect();
    }
    panel.style.top = `${top - Math.round(origin.top)}px`;
    panel.style.left = `${Math.round(left - origin.left)}px`;
  };

  #close = () => {
    if (this.#panel?.matches(':popover-open')) this.#panel.hidePopover();
  };
}

if (!customElements.get('header-dropdown')) {
  customElements.define('header-dropdown', HeaderDropdown);
}
